import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, CheckCircle2, Upload, X, FileText, Paperclip } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { company } from '../mock/data';
import { useI18n } from '../context/I18nContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_FILES = 5;
const MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'zip', 'dwg', 'txt'];

const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/1024/1024).toFixed(1)} MB`;

const Quote = () => {
  const { toast } = useToast();
  const { t } = useI18n();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: '', email: '', company: '', product: '', phone: '', country: '', message: '' });
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const addFiles = (list) => {
    const arr = Array.from(list);
    const merged = [...files];
    for (const f of arr) {
      if (merged.length >= MAX_FILES) { toast({ title: `Max ${MAX_FILES} files`, variant: 'destructive' }); break; }
      const ext = (f.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED.includes(ext)) { toast({ title: `Unsupported file: ${f.name}`, description: `Allowed: ${ALLOWED.join(', ')}`, variant: 'destructive' }); continue; }
      if (f.size > MAX_SIZE) { toast({ title: 'File too large', description: `${f.name} exceeds 5MB`, variant: 'destructive' }); continue; }
      if (merged.some((m) => m.name === f.name && m.size === f.size)) continue;
      merged.push(f);
    }
    setFiles(merged);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ''));
      files.forEach((f) => fd.append('files', f));
      await axios.post(`${API}/quotes`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmitted(true);
      toast({ title: t('quote.success'), description: t('quote.successDesc') });
      setTimeout(() => {
        setSubmitted(false);
        setForm({ name: '', email: '', company: '', product: '', phone: '', country: '', message: '' });
        setFiles([]);
      }, 4000);
    } catch (err) {
      console.error(err);
      toast({ title: t('quote.error'), description: err.response?.data?.detail || t('quote.errorDesc'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="relative py-24 lg:py-32 bg-[#0b2545] overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, #f7a11a 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-[#f7a11a] font-semibold">{t('quote.eyebrow')}</span>
            <h2 className="mt-4 text-4xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight">{t('quote.title')}</h2>
            <p className="mt-6 text-slate-300 text-lg leading-relaxed max-w-lg">{t('quote.desc')}</p>
            <div className="mt-10 space-y-5">
              <a href={`mailto:${company.email}`} className="group flex items-center gap-4 text-white hover:text-[#f7a11a] transition-colors">
                <div className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-[#f7a11a] group-hover:border-[#f7a11a]">
                  <Mail className="w-5 h-5 group-hover:text-slate-900" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50">Email</div>
                  <div className="font-semibold">{company.email}</div>
                </div>
              </a>
              <div className="flex items-center gap-4 text-white">
                <div className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center"><Phone className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50">Phone</div>
                  <div className="font-semibold">{company.phone}</div>
                  <div className="font-semibold">{company.phone2}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-white">
                <div className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center"><MapPin className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50">Head Office</div>
                  <div className="font-semibold">{company.address}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 lg:p-10 shadow-2xl">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center py-10 text-center">
                <div className="w-16 h-16 bg-[#f7a11a] flex items-center justify-center mb-6"><CheckCircle2 className="w-8 h-8 text-slate-900" /></div>
                <h3 className="text-2xl font-black text-slate-900">{t('quote.success')}</h3>
                <p className="mt-3 text-slate-600 max-w-sm">{t('quote.successDesc')}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <h3 className="text-2xl font-black text-slate-900 mb-2">{t('quote.formTitle')}</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t('quote.name')}</label>
                    <Input required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="mt-2 h-12 rounded-none border-slate-300 focus-visible:ring-[#f7a11a] focus-visible:border-[#f7a11a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t('quote.email')}</label>
                    <Input required type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="mt-2 h-12 rounded-none border-slate-300 focus-visible:ring-[#f7a11a] focus-visible:border-[#f7a11a]" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t('quote.company')}</label>
                    <Input value={form.company} onChange={(e)=>setForm({...form, company:e.target.value})} className="mt-2 h-12 rounded-none border-slate-300 focus-visible:ring-[#f7a11a] focus-visible:border-[#f7a11a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t('quote.product')}</label>
                    <Input value={form.product} onChange={(e)=>setForm({...form, product:e.target.value})} className="mt-2 h-12 rounded-none border-slate-300 focus-visible:ring-[#f7a11a] focus-visible:border-[#f7a11a]" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t('quote.phone')}</label>
                    <Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} className="mt-2 h-12 rounded-none border-slate-300 focus-visible:ring-[#f7a11a] focus-visible:border-[#f7a11a]" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t('quote.country')}</label>
                    <Input value={form.country} onChange={(e)=>setForm({...form, country:e.target.value})} className="mt-2 h-12 rounded-none border-slate-300 focus-visible:ring-[#f7a11a] focus-visible:border-[#f7a11a]" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t('quote.message')}</label>
                  <Textarea required rows={4} value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} className="mt-2 rounded-none border-slate-300 focus-visible:ring-[#f7a11a] focus-visible:border-[#f7a11a]" />
                </div>

                {/* File upload */}
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                    <Paperclip className="w-3.5 h-3.5" /> Attach tender / spec documents (optional)
                  </label>
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    onClick={() => fileRef.current?.click()}
                    className={`mt-2 p-6 border-2 border-dashed cursor-pointer text-center transition-colors ${dragging ? 'border-[#f7a11a] bg-[#f7a11a]/5' : 'border-slate-300 hover:border-[#f7a11a] hover:bg-slate-50'}`}
                  >
                    <Upload className="w-6 h-6 mx-auto text-slate-400" />
                    <div className="mt-2 text-sm text-slate-700"><strong>Click to upload</strong> or drag and drop</div>
                    <div className="mt-1 text-[11px] text-slate-500">PDF, DOC, XLS, DWG, PNG, JPG, ZIP · Max 5 files, 5MB each</div>
                    <input ref={fileRef} type="file" multiple accept={ALLOWED.map(e=>'.'+e).join(',')} className="hidden" onChange={(e) => addFiles(e.target.files)} />
                  </div>
                  {files.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {files.map((f, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200">
                          <FileText className="w-4 h-4 text-[#f7a11a] flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-slate-800 truncate">{f.name}</div>
                            <div className="text-[11px] text-slate-500">{fmtSize(f.size)}</div>
                          </div>
                          <button type="button" onClick={() => removeFile(i)} className="p-1 text-slate-400 hover:text-red-500">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-[#0b2545] hover:bg-[#0b2545]/90 text-white h-14 rounded-none font-semibold tracking-widest text-sm group">
                  {loading ? 'SENDING...' : t('cta.submit')}
                  <Send className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Quote;
